<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import HrIcon from '../primitives/HrIcon.vue';
import HrItemLabel from '../primitives/HrItemLabel.vue';
import HrSelect from '../primitives/HrSelect.vue';
import HrInputChip from './HrInputChip.vue';
import HrListItem from './HrListItem.vue';

/**
 * Multi-select — a chip-display select field supporting multiple selections with optional filtering and grouping.
 *
 * A value that no option carries never shows raw: its chip is withheld while `loading` and reads
 * "Unavailable" once loaded, still removable, as in HrListSelect. With `newValueMode` set, a typed
 * value is never in `options` by definition, so it shows as typed.
 *
 * @status recommended
 * @group forms-input
 * @useWhen Selecting multiple values from a list, displayed as removable chips in the field
 * @related HrListSelect
 */
defineOptions({ name: 'HrMultiSelect' });

const props = defineProps({
  /**
   * Visual container style derived from M3 text field variants.
   * @values filled, outlined
   */
  variant: {
    type: String,
    default: 'outlined',
    validator: (v) => ['filled', 'outlined'].includes(v),
  },
  /**
   * Selected values array. Contains `optionValue` values by default, or full option objects when `emitObject` is true.
   * @example ["design","hr"]
   */
  modelValue: {
    type: Array,
    default: () => [],
  },
  /**
   * List of option objects. Supports grouped options via `{ subHeader, options }` entries.
   * @example [{"label":"Design","value":"design","caption":"Brand"},{"label":"HR","value":"hr","caption":"People"},
   * {"label":"IT","value":"it"}]
   */
  options: {
    type: Array,
    default: () => [],
  },
  /** Property name on each option used as the display label. */
  optionLabel: {
    type: String,
    default: 'label',
  },
  /** Property name on each option used as the emitted value when `emitObject` is false. */
  optionValue: {
    type: String,
    default: 'value',
  },
  /** When true, emits the full option object instead of the `optionValue` field. */
  emitObject: {
    type: Boolean,
    default: false,
  },
  /** Floating field label. */
  label: {
    type: String,
    default: null,
  },
  /**
   * Supporting text below the field: one sentence the reader needs to fill it in.
   * A longer or occasional explanation goes behind an HrIconHintBtn, never in a tooltip.
   */
  hint: {
    type: String,
    default: null,
  },
  /** Renders each option's caption above the label instead of below it. */
  captionAbove: {
    type: Boolean,
    default: false,
  },
  /** Shows a text filter input inside the selected-chips area for filtering options. */
  useInput: {
    type: Boolean,
    default: false,
  },
  /** Milliseconds to debounce emitting the filter input value. */
  inputDebounce: {
    type: [String, Number],
    default: 0,
  },
  /** Maximum number of selections allowed; null means unlimited. */
  maxValues: {
    type: Number,
    default: null,
  },
  /**
   * Allows adding new values from typed text. null disables this feature.
   * @values add, add-unique, toggle
   */
  newValueMode: {
    type: [String, null],
    default: null,
    validator: (v) => v === null || ['add', 'add-unique', 'toggle'].includes(v),
  },
  /** Shows a clear button to remove all selections at once. */
  clearable: {
    type: Boolean,
    default: false,
  },
  /** Prevents interaction and dims the field. */
  disable: {
    type: Boolean,
    default: false,
  },
  /** Makes the field non-editable while keeping it visually active. */
  readonly: {
    type: Boolean,
    default: false,
  },
  /** Shows a loading indicator inside the field. */
  loading: {
    type: Boolean,
    default: false,
  },
  /** Puts the field into error state. */
  error: {
    type: Boolean,
    default: false,
  },
  /** Error message shown below the field when `error` is true. */
  errorMessage: {
    type: String,
    default: null,
  },
  /** Validation rule functions applied to the field value. */
  rules: {
    type: Array,
    default: null,
  },
  /** When true, validation runs only after the first blur. */
  lazyRules: {
    type: Boolean,
    default: false,
  },
  /** Suppresses the reserved space below the field for hint/error messages. */
  hideBottomSpace: {
    type: Boolean,
    default: false,
  },
  /** Reduces the field to the M3 dense density level. */
  dense: {
    type: Boolean,
    default: false,
  },
  /**
   * Values that stay selected: their chips show no remove button, and removing one by keyboard or
   * from the dropdown is undone. Given as option values, also when `emitObject` is set.
   */
  lockedValues: {
    type: Array,
    default: () => [],
  },
  /** Hides the checkmark shown on selected options in the dropdown. */
  noCheckmark: {
    type: Boolean,
    default: false,
  },
  /**
   * Legacy props forwarded to the underlying Quasar component.
   * @deprecated Only for backwards compatibility with the legacy theme — ignored when a design system theme is active.
   */
  legacyProps: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits({
  /**
   * Emitted when the selection changes; payload is the new array of selected values
   * (or objects when `emitObject` is true).
   */
  'update:modelValue': null,
});

const flattenedOptions = computed(() => props.options.flatMap((entry) => {
  if (entry !== null && typeof entry === 'object' && 'subHeader' in entry) {
    return [{ _header: true, label: entry.subHeader, disable: true }, ...entry.options];
  }
  return [entry];
}));

const { t } = useI18n();

const isRawValue = (opt) => (opt === null) || (typeof opt !== 'object');

// QSelect hands `selected-item` the raw value, usually an id, when no option carries it. A field
// that accepts typed values is the exception: there the raw value is the label.
function isUnresolved(opt) {
  return (props.newValueMode === null) && isRawValue(opt);
}

function getChipLabel(opt) {
  if (isUnresolved(opt)) return t('common.labelUnavailable');
  return isRawValue(opt) ? String(opt ?? '') : opt[props.optionLabel];
}

const valueOf = (entry) => (isRawValue(entry) ? entry : entry[props.optionValue]);

const isLocked = (entry) => props.lockedValues.includes(valueOf(entry));

// A locked value goes back to its old position rather than the end, so the chips don't reorder.
function onUpdate(value) {
  const next = value ?? [];
  const nextValues = next.map(valueOf);
  const dropsLocked = props.modelValue.some((entry) => isLocked(entry) && !nextValues.includes(valueOf(entry)));

  if (!dropsLocked) {
    emit('update:modelValue', value);
    return;
  }

  const kept = props.modelValue.filter((entry) => isLocked(entry) || nextValues.includes(valueOf(entry)));
  const keptValues = kept.map(valueOf);
  const added = next.filter((entry) => !keptValues.includes(valueOf(entry)));

  if (added.length === 0 && kept.length === props.modelValue.length) return;
  emit('update:modelValue', [...kept, ...added]);
}

const selectRef = ref(null);
defineExpose({
  /** Closes the options dropdown. */
  hidePopup: () => selectRef.value?.hidePopup(),
  /** Adds a value to the selection; second argument makes the add unique. */
  add: (...args) => selectRef.value?.add(...args),
  /** Runs the field's validation rules; returns a boolean or a Promise resolving to one. */
  validate: (...args) => selectRef.value?.validate(...args),
});
</script>

<template>
  <hr-select
    ref="selectRef"
    :variant="variant"
    :model-value="modelValue"
    :options="flattenedOptions"
    :label="label"
    :hint="hint"
    :option-label="optionLabel"
    :option-value="optionValue"
    :emit-value="!emitObject"
    :map-options="!emitObject"
    multiple
    :use-input="useInput"
    :input-debounce="inputDebounce"
    :max-values="maxValues"
    :new-value-mode="newValueMode"
    :clearable="clearable"
    :disable="disable"
    :readonly="readonly"
    :loading="loading"
    :aria-busy="loading || null"
    :error="error"
    :error-message="errorMessage"
    :rules="rules"
    :lazy-rules="lazyRules"
    :hide-bottom-space="hideBottomSpace"
    :dense="dense"
    :legacy-props="legacyProps"
    @update:model-value="onUpdate"
  >
    <template #selected-item="scope">
      <hr-input-chip
        v-if="!(loading && isUnresolved(scope.opt))"
        size="sm"
        :label="getChipLabel(scope.opt)"
        :disable="disable || readonly"
        :hide-remove="isLocked(scope.opt)"
        style="margin: 4px 4px"
        @remove="scope.removeAtIndex(scope.index)"
      />
    </template>

    <template #option="scope">
      <hr-item-label
        v-if="scope.opt._header"
        header
      >
        {{ scope.opt.label }}
      </hr-item-label>
      <hr-list-item
        v-else
        v-bind="scope.itemProps"
        :label="scope.opt[optionLabel] ?? null"
        :caption="scope.opt.caption ?? null"
        :icon="scope.opt.icon ?? null"
        :trailing-text="scope.opt.trailingText ?? null"
        :caption-above="captionAbove"
      >
        <template
          v-if="!noCheckmark && scope.selected"
          #trailing
        >
          <hr-icon name="check" />
        </template>
      </hr-list-item>
    </template>
  </hr-select>
</template>

<style lang="scss" scoped>
/*
 * Chip-set spacing vs. field height. Each chip carries 4px all round, so chips
 * that wrap onto a second row get the M3 8dp gap. Canceling the outer 4px
 * vertically here makes a single row of chips height-neutral, so the field lands
 * on the standard 56px control height and lines up with a single-line field
 * beside it — while wrapped rows keep their gap.
 */
:deep(.q-field__native) {
  margin-top: -4px;
  margin-bottom: -4px;
}
</style>
